import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { clearDeferredPromptEvent, isAppInstalled, setDeferredPromptEvent } from "./installController";

const LS_KEY_DISMISSED_AT = "pwa_install_prompt_dismissed_at";
const DISMISS_FOR_DAYS = 7;

function isStandalone() {
  return isAppInstalled();
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua);
}

function isSafari() {
  const ua = window.navigator.userAgent || "";
  const isAppleWebKit = /applewebkit/i.test(ua);
  const isNotChrome = !/crios|chrome|chromium|fxios|edgios/i.test(ua);
  return isAppleWebKit && isNotChrome;
}

function dismissedRecently() {
  const raw = window.localStorage.getItem(LS_KEY_DISMISSED_AT);
  const ts = raw ? Number(raw) : 0;
  if (!ts || Number.isNaN(ts)) return false;
  const ms = DISMISS_FOR_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - ts < ms;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => dismissedRecently());
  const [installing, setInstalling] = useState(false);

  const isIosSafari = useMemo(() => isIos() && isSafari(), []);

  useEffect(() => {
    setInstalled(isStandalone());

    const onBeforeInstallPrompt = (e) => {
      // Stop Chrome from showing its own mini-infobar.
      e.preventDefault();
      setDeferredPrompt(e);
      setDeferredPromptEvent(e);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      clearDeferredPromptEvent();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const canShowAndroid = !!deferredPrompt && !installed;
  const canShowIos = isIosSafari && !installed;
  const canShow = (canShowAndroid || canShowIos) && !dismissed;

  const handleDismiss = () => {
    window.localStorage.setItem(LS_KEY_DISMISSED_AT, String(Date.now()));
    setDismissed(true);
    setDeferredPrompt(null);
    clearDeferredPromptEvent();
  };

  const handleInstall = async () => {
    if (!deferredPrompt || installing) return;
    setInstalling(true);
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      clearDeferredPromptEvent();
      setInstalling(false);
    }
  };

  if (!canShow) return null;

  return (
    <Dialog
      open
      onClose={handleDismiss}
      aria-labelledby="pwa-install-dialog-title"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="pwa-install-dialog-title" sx={{ pb: 1 }}>
        Install RealX
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1}>
          {canShowAndroid ? (
            <Typography variant="body2" color="text.secondary">
              Install the app for faster access and a full-screen experience.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              On iPhone: tap Share, then “Add to Home Screen”.
            </Typography>
          )}
          <Box />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleDismiss} sx={{ textTransform: "none" }}>
          Not now
        </Button>
        {canShowAndroid && (
          <Button variant="contained" onClick={handleInstall} disabled={installing} sx={{ textTransform: "none" }}>
            {installing ? "Installing…" : "Install"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

